import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { BucketBadge, type Bucket } from '@/components/BucketBadge';
import { SignalCoverage } from '@/components/SignalCoverage';
import { useApiQuery, type DashboardResponse, type BriefResponse } from '@/lib/api';

const BUCKET_ORDER: Bucket[] = ['Engaged', 'Watching', 'AtRisk', 'Slipping'];

export function Dashboard() {
  const { data, loading, error } = useApiQuery<DashboardResponse>('/api/dashboard');
  const brief = useApiQuery<BriefResponse>('/api/brief?limit=6');
  const counts = data?.counts;
  const totalScored = counts
    ? counts.Engaged + counts.Watching + counts.AtRisk + counts.Slipping
    : 0;

  return (
    <div className="container mx-auto max-w-7xl px-6 py-10">
      <header className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">Overview</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">Engagement dashboard</h1>
          <p className="mt-2 text-muted-foreground">
            {error ? 'Could not load live data.' : 'Live from the ChamberPulse data foundation.'}
          </p>
        </div>
        {loading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
      </header>

      {error && (
        <p className="mt-6 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</p>
      )}

      {data?.synced && (
        <section className="mt-8 grid gap-4 sm:grid-cols-3">
          <article className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Members synced</p>
            <p className="mt-2 text-3xl font-bold">{data.synced.members.toLocaleString()}</p>
          </article>
          <article className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Events synced</p>
            <p className="mt-2 text-3xl font-bold">{data.synced.events.toLocaleString()}</p>
          </article>
          <article className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Last sync</p>
            <p className="mt-2 text-lg font-semibold">{data.lastSync?.status ?? '—'}</p>
            <p className="text-xs text-muted-foreground">
              {data.lastSync?.endedAt ? new Date(data.lastSync.endedAt).toLocaleString() : ''}
            </p>
          </article>
        </section>
      )}

      <h2 className="mt-10 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
        Engagement segmentation
        {data?.scoringPending && (
          <span className="ml-2 font-normal normal-case text-muted-foreground/80">— awaiting scorer</span>
        )}
      </h2>

      <section className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {BUCKET_ORDER.map((bucket) => {
          const n = counts?.[bucket];
          return (
            <Link
              key={bucket}
              to={`/members?bucket=${bucket}`}
              className="rounded-xl border border-border bg-card p-5 shadow-sm transition-colors hover:border-primary/40 hover:bg-muted/30"
            >
              <div className="flex items-center justify-between">
                <BucketBadge bucket={bucket} />
              </div>
              <p className="mt-4 text-3xl font-bold tabular-nums">
                {n === undefined ? (loading ? '—' : 0) : n.toLocaleString()}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                members
                {n !== undefined && totalScored > 0 && (
                  <span className="ml-1.5 text-xs">
                    · {Math.round((n / totalScored) * 100)}% of book
                  </span>
                )}
              </p>
            </Link>
          );
        })}
      </section>

      <section className="mt-10 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-semibold">What we can see</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Which engagement signals each member actually shows, across the last year.
          </p>
          {data?.stats ? (
            <SignalCoverage signals={data.stats.signals} className="mt-6" />
          ) : (
            <p className="mt-6 rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
              {loading ? 'Loading…' : 'Run a full rescore to publish signal coverage.'}
            </p>
          )}
          {data?.stats && data.stats.signals.recencyUnknown > 0 && (
            <p className="mt-4 text-xs text-muted-foreground">
              {data.stats.signals.recencyUnknown.toLocaleString()} members have no usable activity
              date in the source data. Their recency weight is redistributed rather than counted
              against them.
            </p>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Call list</h2>
            <Link to="/brief" className="text-xs font-medium text-primary hover:underline">
              View all →
            </Link>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">Top at-risk members worth a personal touch.</p>
          <ul className="mt-4 divide-y divide-border text-sm">
            {brief.loading && (
              <li className="py-3 text-center text-muted-foreground">
                <Loader2 className="mx-auto h-4 w-4 animate-spin" />
              </li>
            )}
            {!brief.loading && (brief.data?.items?.length ?? 0) === 0 && (
              <li className="py-3 text-muted-foreground">No at-risk members surfaced.</li>
            )}
            {!brief.loading &&
              brief.data?.items?.map((m) => (
                <li key={m.profileId} className="py-3">
                  <Link to={`/members/${m.profileId}`} className="font-medium text-primary hover:underline">
                    {m.legalName ?? m.profileId}
                  </Link>
                  <p className="text-xs text-muted-foreground">{m.reasons[0] ?? m.suggestedAction}</p>
                </li>
              ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
